import type {
  DevelopmentRole,
  FileAttachment,
  ProjectData,
  ProjectNode,
  VisibilityTier,
  Workstream,
  ClassificationSource,
} from '@/shared/types/projectData';
import { buildCanonicalRootNodeIds, sanitizeProjectData } from '@/shared/utils/projectDataUtils';
import { getProjectIdFromNodeId } from '@/features/projects/MultiProjectContext';

type RealtimeProjectionUser = {
  id?: string;
  username?: string;
  role?: string | null;
} | null;

type EffectiveNodeMetadata = {
  workstream: Workstream;
  developmentRoles: DevelopmentRole[];
  visibilityTier: VisibilityTier;
  visibleToAccountIds: string[];
  hasAccountRestriction: boolean;
  classificationSource: ClassificationSource;
  classificationConfidence: number | null;
};

const DEVELOPMENT_ROLE_VALUES: DevelopmentRole[] = [
  'architecture',
  'planning',
  'ux',
  'ui',
  'frontend',
  'backend',
  'fullstack',
  'mobile',
  'qa',
  'devops',
  'data',
  'ai_ml',
  'security',
  'integration',
];

const TIER_ALLOWED_PRINCIPALS: Record<VisibilityTier, Set<string>> = {
  internal: new Set(['admin', 'employee']),
  partner: new Set(['admin', 'employee', 'partner']),
  client: new Set(['admin', 'employee', 'partner', 'client']),
};

const normalizeVisibilityTier = (value?: string | null): VisibilityTier => {
  if (value === 'partner' || value === 'client') {
    return value;
  }
  return 'internal';
};

const normalizeWorkstream = (value?: string | null): Workstream => {
  if (value === 'development' || value === 'meeting' || value === 'sales' || value === 'admin' || value === 'planning' || value === 'research') {
    return value;
  }
  return 'general';
};

const normalizeClassificationSource = (value?: string | null): ClassificationSource => {
  return value === 'llm_suggested' || value === 'heuristic' ? value : 'manual';
};

const normalizeClassificationConfidence = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return Math.max(0, Math.min(1, parsed));
};

const normalizeVisibleToAccountIds = (value: unknown): string[] => {
  if (value === null || value === undefined) {
    return [];
  }

  const rawValues = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [value];

  const normalized: string[] = [];
  const seen = new Set<string>();

  rawValues.forEach((raw) => {
    const cleaned = String(raw || '').trim();
    if (!cleaned || seen.has(cleaned)) {
      return;
    }
    seen.add(cleaned);
    normalized.push(cleaned);
  });

  return normalized;
};

const normalizeDevelopmentRoles = (value: unknown): DevelopmentRole[] => {
  if (value === null || value === undefined) {
    return [];
  }

  const rawValues = Array.isArray(value)
    ? value
    : typeof value === 'string'
      ? value.split(',')
      : [value];

  const allowed = new Set<DevelopmentRole>(DEVELOPMENT_ROLE_VALUES);
  const normalized: DevelopmentRole[] = [];
  const seen = new Set<string>();

  rawValues.forEach((raw) => {
    const cleaned = String(raw || '').trim().toLowerCase() as DevelopmentRole;
    if (!cleaned || seen.has(cleaned) || !allowed.has(cleaned)) {
      return;
    }
    seen.add(cleaned);
    normalized.push(cleaned);
  });

  return normalized;
};

const getPrincipalType = (user: RealtimeProjectionUser): string => {
  const role = typeof user?.role === 'string' ? user.role.trim().toLowerCase() : '';
  return role || 'employee';
};

const getCurrentUserAccountIds = (user: RealtimeProjectionUser): Set<string> => {
  const ids = new Set<string>();
  [user?.id, user?.username].forEach((raw) => {
    const cleaned = typeof raw === 'string' ? raw.trim() : '';
    if (cleaned) {
      ids.add(cleaned);
    }
  });
  return ids;
};

const getProjectVisibilityDefault = (
  projectData: ProjectData,
  projectVisibilityDefaults: Record<string, VisibilityTier | undefined>,
  projectId?: string | null
): VisibilityTier => {
  if (projectId && projectVisibilityDefaults[projectId]) {
    return normalizeVisibilityTier(projectVisibilityDefaults[projectId]);
  }
  return normalizeVisibilityTier(projectData.metadata?.settings?.visibilityTierDefault);
};

const getNodeProjectId = (node: ProjectNode, projectData: ProjectData): string => {
  if (typeof node.projectId === 'string' && node.projectId) {
    return node.projectId;
  }
  if (typeof node.id === 'string' && node.id.includes(':')) {
    return getProjectIdFromNodeId(node.id, projectData.projectId);
  }
  return projectData.projectId;
};

const extractNodeMetadata = (
  node: ProjectNode,
  defaultVisibilityTier: VisibilityTier
): EffectiveNodeMetadata => {
  const customFields = typeof node.customFields === 'object' && node.customFields
    ? node.customFields
    : {};
  const rawVisibleToAccountIds = node.visibleToAccountIds !== undefined
    ? node.visibleToAccountIds
    : customFields.visibleToAccountIds !== undefined
      ? customFields.visibleToAccountIds
      : customFields.visible_to_account_ids;
  const visibleToAccountIds = normalizeVisibleToAccountIds(rawVisibleToAccountIds);

  return {
    workstream: normalizeWorkstream(
      node.workstream || (customFields.workstream as string | undefined) || null
    ),
    developmentRoles: normalizeDevelopmentRoles(
      node.developmentRoles ||
      customFields.developmentRoles ||
      customFields.development_roles
    ),
    visibilityTier: normalizeVisibilityTier(
      node.visibilityTier ||
      (customFields.visibilityTier as string | undefined) ||
      (customFields.visibility_tier as string | undefined) ||
      defaultVisibilityTier
    ),
    visibleToAccountIds,
    hasAccountRestriction: visibleToAccountIds.length > 0,
    classificationSource: normalizeClassificationSource(
      (node.classificationSource as string | undefined) ||
      (customFields.classificationSource as string | undefined) ||
      (customFields.classification_source as string | undefined) ||
      'manual'
    ),
    classificationConfidence: normalizeClassificationConfidence(
      node.classificationConfidence ??
      customFields.classificationConfidence ??
      customFields.classification_confidence
    ),
  };
};

const applyInheritedMetadata = (
  node: ProjectNode,
  parentMetadata: EffectiveNodeMetadata | undefined,
  defaultVisibilityTier: VisibilityTier
): EffectiveNodeMetadata => {
  const nodeMetadata = extractNodeMetadata(
    node,
    parentMetadata?.visibilityTier || defaultVisibilityTier
  );
  const effectiveDevelopmentRoles =
    nodeMetadata.workstream === 'development' ? nodeMetadata.developmentRoles : [];

  const inheritedAllowedIds = parentMetadata?.visibleToAccountIds || [];
  const nodeAllowedIds = nodeMetadata.visibleToAccountIds;
  const inheritedRestriction = parentMetadata?.hasAccountRestriction === true;
  const nodeRestriction = nodeMetadata.hasAccountRestriction;

  let effectiveAllowedIds = nodeAllowedIds;
  if (inheritedRestriction && nodeRestriction) {
    const inheritedSet = new Set(inheritedAllowedIds);
    effectiveAllowedIds = nodeAllowedIds.filter((id) => inheritedSet.has(id));
  } else if (inheritedRestriction) {
    effectiveAllowedIds = [...inheritedAllowedIds];
  }

  return {
    ...nodeMetadata,
    developmentRoles: effectiveDevelopmentRoles,
    visibleToAccountIds: effectiveAllowedIds,
    hasAccountRestriction: inheritedRestriction || nodeRestriction,
  };
};

const isCanonicalId = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0 && value.trim() === value;

const isMetadataVisibleToUser = (
  metadata: EffectiveNodeMetadata,
  user: RealtimeProjectionUser,
  projectOwnerId?: string
): boolean => {
  const principalType = getPrincipalType(user);
  if (principalType === 'admin') {
    return true;
  }

  // Backend ownership is a user ID. Deliberately do not use username here:
  // usernames are not a project-authority identity and may be shared or stale.
  if (isCanonicalId(user?.id) && isCanonicalId(projectOwnerId) && user.id === projectOwnerId) {
    return true;
  }

  const allowedIds = metadata.visibleToAccountIds;
  if (metadata.hasAccountRestriction) {
    const currentUserAccountIds = getCurrentUserAccountIds(user);
    return allowedIds.some((id) => currentUserAccountIds.has(id));
  }

  return TIER_ALLOWED_PRINCIPALS[metadata.visibilityTier].has(principalType);
};

const recomputeVisibleFiles = (
  files: FileAttachment[],
  visibleNodeIds: Set<string>
): FileAttachment[] => {
  return files.filter((file) => {
    const metadata = typeof file.metadata === 'object' && file.metadata ? file.metadata : { associatedDocIds: [] };
    const associatedDocIds = normalizeVisibleToAccountIds(
      metadata.associatedDocIds || (metadata as Record<string, unknown>).associated_doc_ids
    );
    const associatedNodeId = String(
      (metadata as Record<string, unknown>).associatedNodeId ||
      (metadata as Record<string, unknown>).associated_node_id ||
      ''
    ).trim();

    if (associatedDocIds.length > 0 && !associatedDocIds.some((docId) => visibleNodeIds.has(docId))) {
      return false;
    }

    if (associatedNodeId && !visibleNodeIds.has(associatedNodeId)) {
      return false;
    }

    return true;
  });
};

const recomputeReportSummary = (nodes: ProjectNode[]) => {
  const reportNodes = nodes
    .filter((node) =>
      Boolean(node.customFields?.isReport || node.customFields?.is_report)
    )
    .sort((left, right) => {
      const leftTime = String(left.updatedAt || left.createdAt || '');
      const rightTime = String(right.updatedAt || right.createdAt || '');
      return rightTime.localeCompare(leftTime);
    });

  const latest = reportNodes[0];
  return {
    count: reportNodes.length,
    latestReportId: latest?.id || null,
    latestReportTitle: latest?.title || null,
    latestUpdatedAt: latest ? String(latest.updatedAt || latest.createdAt || '') : null,
  };
};

export function projectDataNeedsRealtimeVisibilityProjection(
  projectData: ProjectData,
  user: RealtimeProjectionUser
): boolean {
  const principalType = getPrincipalType(user);
  if (principalType === 'admin') {
    return false;
  }
  if (principalType === 'employee') {
    return projectData.nodes.some((node) => {
      const defaultVisibilityTier = getProjectVisibilityDefault(
        projectData,
        {},
        getNodeProjectId(node, projectData)
      );
      return extractNodeMetadata(node, defaultVisibilityTier).hasAccountRestriction;
    });
  }
  return true;
}

export function filterRealtimeProjectDataForUser(
  projectData: ProjectData,
  user: RealtimeProjectionUser,
  projectVisibilityDefaults: Record<string, VisibilityTier | undefined> = {},
  projectOwnerIds: Record<string, string | undefined> = {}
): ProjectData {
  if (!user || !projectDataNeedsRealtimeVisibilityProjection(projectData, user)) {
    return projectData;
  }

  const nodesById = new Map<string, ProjectNode>();
  const childIdsByParent = new Map<string | null, string[]>();

  projectData.nodes.forEach((node) => {
    nodesById.set(node.id, node);
    const parentId = node.parentId || null;
    const children = childIdsByParent.get(parentId) || [];
    children.push(node.id);
    childIdsByParent.set(parentId, children);
  });

  const effectiveMetadataById = new Map<string, EffectiveNodeMetadata>();
  const visited = new Set<string>();

  const walk = (nodeId: string, parentMetadata?: EffectiveNodeMetadata): void => {
    const node = nodesById.get(nodeId);
    if (!node || visited.has(nodeId)) {
      return;
    }

    visited.add(nodeId);
    const defaultVisibilityTier = getProjectVisibilityDefault(
      projectData,
      projectVisibilityDefaults,
      getNodeProjectId(node, projectData)
    );
    const effectiveMetadata = applyInheritedMetadata(node, parentMetadata, defaultVisibilityTier);
    effectiveMetadataById.set(nodeId, effectiveMetadata);

    (childIdsByParent.get(nodeId) || []).forEach((childId) => walk(childId, effectiveMetadata));
  };

  (childIdsByParent.get(null) || []).forEach((rootId) => walk(rootId));

  projectData.nodes.forEach((node) => {
    if (!visited.has(node.id)) {
      walk(node.id);
    }
  });

  const visibleNodeIds = new Set<string>();
  projectData.nodes.forEach((node) => {
    const effectiveMetadata = effectiveMetadataById.get(node.id);
    const projectId = getNodeProjectId(node, projectData);
    const projectOwnerId = isCanonicalId(projectId) ? projectOwnerIds[projectId] : undefined;
    if (effectiveMetadata && isMetadataVisibleToUser(effectiveMetadata, user, projectOwnerId)) {
      visibleNodeIds.add(node.id);
    }
  });

  const findNearestVisibleAncestor = (nodeId: string): string | undefined => {
    const seen = new Set<string>();
    let currentParentId = nodesById.get(nodeId)?.parentId;

    while (currentParentId && !seen.has(currentParentId)) {
      if (visibleNodeIds.has(currentParentId)) {
        return currentParentId;
      }
      seen.add(currentParentId);
      currentParentId = nodesById.get(currentParentId)?.parentId;
    }

    return undefined;
  };

  const filteredNodes = projectData.nodes
    .filter((node) => visibleNodeIds.has(node.id))
    .map((node) => {
      const effectiveMetadata = effectiveMetadataById.get(node.id);
      const reparentedParentId = findNearestVisibleAncestor(node.id);
      const customFields = typeof node.customFields === 'object' && node.customFields
        ? { ...node.customFields }
        : {};
      const isAdmin = getPrincipalType(user) === 'admin';
      const projectedNode = { ...node };
      if (!isAdmin) {
        delete projectedNode.visibleToAccountIds;
        delete (projectedNode as ProjectNode & { visible_to_account_ids?: unknown }).visible_to_account_ids;
      }

      if (effectiveMetadata) {
        customFields.workstream = effectiveMetadata.workstream;
        if (effectiveMetadata.workstream === 'development') {
          customFields.developmentRoles = [...effectiveMetadata.developmentRoles];
          customFields.development_roles = [...effectiveMetadata.developmentRoles];
        } else {
          delete customFields.developmentRoles;
          delete customFields.development_roles;
        }
        customFields.visibilityTier = effectiveMetadata.visibilityTier;
        customFields.visibility_tier = effectiveMetadata.visibilityTier;
        if (isAdmin) {
          customFields.visibleToAccountIds = [...effectiveMetadata.visibleToAccountIds];
          customFields.visible_to_account_ids = [...effectiveMetadata.visibleToAccountIds];
        } else {
          delete customFields.visibleToAccountIds;
          delete customFields.visible_to_account_ids;
        }
        customFields.classificationSource = effectiveMetadata.classificationSource;
        customFields.classification_source = effectiveMetadata.classificationSource;
        customFields.classificationConfidence = effectiveMetadata.classificationConfidence;
        customFields.classification_confidence = effectiveMetadata.classificationConfidence;
      }

      return {
        ...projectedNode,
        ...(effectiveMetadata
          ? {
              workstream: effectiveMetadata.workstream,
              developmentRoles: [...effectiveMetadata.developmentRoles],
              visibilityTier: effectiveMetadata.visibilityTier,
              ...(isAdmin ? { visibleToAccountIds: [...effectiveMetadata.visibleToAccountIds] } : {}),
              classificationSource: effectiveMetadata.classificationSource,
              classificationConfidence: effectiveMetadata.classificationConfidence,
            }
          : {}),
        parentId: reparentedParentId,
        dependencies: Array.isArray(node.dependencies)
          ? node.dependencies.filter((dependencyId) => visibleNodeIds.has(dependencyId))
          : [],
        linkedDocs: Array.isArray(node.linkedDocs)
          ? node.linkedDocs.filter((linkedId) => visibleNodeIds.has(linkedId))
          : [],
        customFields,
      } satisfies ProjectNode;
    });

  const rootNodeIds = buildCanonicalRootNodeIds(filteredNodes);
  const files = Array.isArray(projectData.files)
    ? recomputeVisibleFiles(projectData.files, visibleNodeIds)
    : [];

  const nextData: ProjectData = {
    ...projectData,
    nodes: filteredNodes,
    rootNodeIds,
    files,
    metadata: {
      ...projectData.metadata,
      settings: {
        ...projectData.metadata?.settings,
        reportSummary: recomputeReportSummary(filteredNodes),
      },
    },
  };

  return sanitizeProjectData(nextData, { logRepairs: false });
}
